/**
 * WB4 Depth Layer Phase A — Workbench timeline API client.
 *
 * **Client-only outbox path (ActionExecutor golden path):**
 * `appendWorkbenchTimelineEventOutboxFirst` POSTs to
 * `/api/workspace/workbench-timeline/events`, which runs server
 * `appendTimelineEventOutboxFirst` (outbox enqueue + immediate fan-out).
 * No domain mutation on the client; not txn-coupled with domain rows.
 *
 * **Server-mutation paths** (correction · revision · thread · domain bridge)
 * enqueue outbox inside `runWorkbenchTransaction` with domain writes — same
 * outbox fan-out pipeline, different entry point.
 */

import { resolveApiOrigin } from '@shared/api/resolve-api-origin';
import type {
  WorkbenchAuxiliarySummary,
  WorkbenchTimelineEvent,
  WorkbenchTimelineEventInput,
  WorkbenchTimelineObjectRef,
} from '@shared/business-workbench/workbench-timeline-event';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { fetchWithAuth } from './authFetch';

const getBaseUrl = (): string => resolveApiOrigin();

function timelineObjectPath(ref: WorkbenchTimelineObjectRef): string {
  return `/api/workspace/workbench-timeline/objects/${encodeURIComponent(ref.domain)}/${encodeURIComponent(ref.objectType)}/${encodeURIComponent(ref.objectId)}`;
}

export async function fetchWorkbenchTimelineEvents(input: {
  tenantId: string;
  objectRef: WorkbenchTimelineObjectRef;
  limit?: number;
}): Promise<WorkbenchTimelineEvent[]> {
  const params = new URLSearchParams();
  if (input.limit !== undefined) {
    params.set('limit', String(input.limit));
  }
  const query = params.toString();
  const url = `${getBaseUrl()}${timelineObjectPath(input.objectRef)}/timeline${query ? `?${query}` : ''}`;
  const res = await fetchWithAuth(url, {
    method: 'GET',
    headers: { 'x-tenant-id': input.tenantId },
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const body = (await res.json()) as {
    success?: boolean;
    data?: { events?: WorkbenchTimelineEvent[] };
  };
  if (!res.ok || body.success === false) {
    throw new Error('workbench timeline fetch failed');
  }
  return body.data?.events ?? [];
}

/** Client outbox-first timeline append — server runs `appendTimelineEventOutboxFirst`. */
export async function appendWorkbenchTimelineEventOutboxFirst(
  input: WorkbenchTimelineEventInput,
  options?: { auditRequired?: boolean }
): Promise<WorkbenchTimelineEvent | null> {
  const url = `${getBaseUrl()}/api/workspace/workbench-timeline/events`;
  const res = await fetchWithAuth(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': input.tenantId,
    },
    body: JSON.stringify({
      ...input,
      ...(options?.auditRequired ? { auditRequired: true } : {}),
    }),
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const body = (await res.json()) as {
    success?: boolean;
    data?: { event?: WorkbenchTimelineEvent };
  };
  if (!res.ok || body.success === false || !body.data?.event) {
    throw new Error('workbench timeline append failed');
  }
  return body.data.event;
}

/** @deprecated Use `appendWorkbenchTimelineEventOutboxFirst`. */
export const appendWorkbenchTimelineEventRemote = appendWorkbenchTimelineEventOutboxFirst;

export async function fetchWorkbenchAuxiliarySummary(input: {
  tenantId: string;
  objectRef: WorkbenchTimelineObjectRef;
}): Promise<WorkbenchAuxiliarySummary> {
  const url = `${getBaseUrl()}${timelineObjectPath(input.objectRef)}/auxiliary-summary`;
  const res = await fetchWithAuth(url, {
    method: 'GET',
    headers: { 'x-tenant-id': input.tenantId },
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const body = (await res.json()) as {
    success?: boolean;
    data?: { summary?: WorkbenchAuxiliarySummary };
  };
  if (!res.ok || body.success === false || !body.data?.summary) {
    throw new Error('workbench auxiliary summary fetch failed');
  }
  return body.data.summary;
}
